import React, { useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { IntlContext, FlashContext } from "context";
import formatErrorResponse from "helpers/form/formatErrorResponse";
import { fetchCollaborator, updateCollaborator } from "hooks/collaborator";
import { Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import Button from "ui/Button";
import ProfileFormFields from "./FormFields";
import { useParams } from "react-router";
import { Loader } from "ui/Loader";

interface Props {
    onEditSubmitSuccess: () => void;
}

const CollaboratorCommonEdit: React.FC<Props> = ({ onEditSubmitSuccess }) => {
    const intl = useContext(IntlContext);
    const flashMessage = useContext(FlashContext);
    const { id } = useParams();
    const { isLoading, data } = useQuery({
        queryKey: ["collaborator", id],
        queryFn: () => fetchCollaborator(id as string),
        enabled: !!id,
    });

    if (isLoading || !data) {
        return <Loader />;
    }

    type CollaboratorFormValues = {
        gender?: string;
        last_name?: string;
        first_name?: string;
        phone?: string;
        address?: string;
        email?: string;
        is_attach_company?: "0" | "1";
        resources?: {
            client?: {
                company_id?: string;
                company_name?: string;
                code?: string;
                has_pet?: "0" | "1";
                collaborator_status?: "0" | "1";
                services?: string[];
                comment?: string;
                birthday?: string;
            };
            client_company?: {
                code?: string;
            };
        };
    };

    const isRecord = (value: unknown): value is Record<string, unknown> =>
        typeof value === "object" && value !== null;

    const onSubmit = async (values: CollaboratorFormValues) => {
        try {
            if (id) {
                await updateCollaborator(id, values);
                flashMessage?.addSuccess(intl.formatMessage("profile.user.data.update"));
                onEditSubmitSuccess();
            }
        } catch (err: unknown) {
            if (isRecord(err) && "response" in err && isRecord(err.response) && "data" in err.response) {
                const responseData = err.response.data;
                if (isRecord(responseData) && "data" in responseData) {
                    return formatErrorResponse(responseData as { data: unknown });
                }
            }
            throw err;
        }
    };

    const initialValues = {
        gender: data?.gender,
        last_name: data?.last_name,
        first_name: data?.first_name,
        phone: data?.phone,
        address: data?.address,
        email: data?.email,
        is_attach_company: data?.resources?.client?.company_id ? "1" : "0",
        resources: {},
    };

    initialValues.resources = {
        client: {
            company_id: data?.resources?.client?.company_id,
            company_name: data?.resources?.client.company_name,
            code: data?.resources?.client.company_code,
            has_pet: data?.resources?.client.has_pet ? "1" : "0",
            collaborator_status: data?.resources?.client.has_pet ? "1" : "0",
            services: data?.resources?.client.services,
            comment: data?.resources?.client.comment,
            birthday: data?.resources?.client.birthday,
        },
        client_company: {
            code: data?.resources?.client_company.code,
        },
    };

    return (
        <Form
            onSubmit={onSubmit}
            initialValues={initialValues}
            mutators={{ ...arrayMutators }}
            render={({ handleSubmit, submitting }) => (
                <form onSubmit={handleSubmit} className="register__form">
                    <ProfileFormFields />
                    <div className="text-center margin-top--3 padding-top--3">
                        <Button className="btn btn__outline--primary" type="submit" disabled={submitting}>
                            {intl.formatMessage("generic.action.save")}
                        </Button>
                    </div>
                </form>
            )}
        />
    );
};

export default CollaboratorCommonEdit;
